<script setup>
import { computed } from 'vue';
import HintContainer from "@/components/alerts/HintContainer";
import { useFormData } from "@/composables/useFormData";
import { useAuthService } from "~/composables/useAuthService";
import { aboveLimitText, belowLimitText } from "~/utils/ladderChallengeSummary";

const { challengeRejectionRuleToText, isSwapLadder } = useFormData();
const { isAuthenticated } = useAuthService();
const { t } = useI18n();

const props = defineProps({
  unrestrictedChallenges: {
    type: Number,
    default: null,
  },
  ladder: {
    type: Object,
    required: true,
  },
  showSquashLevels : {
    type: Boolean,
    default : false
  },
  autoApprove: {
    type: Boolean,
    default: false
  },
  scoringSystemName: {
    type: String,
    default: null
  }
});

// Singles ladders (1-a-side) talk about players, not teams.
const singles = computed(() => (props.ladder.playersPerTeam ?? 1) <= 1);
const unit = computed(() => singles.value ? 'player' : 'team');
const units = computed(() => singles.value ? 'players' : 'teams');
// The server counts CHALLENGEABLE teams between the two rungs — frozen teams
// and teams sharing one of the challenger's players are skipped.
const skipRule = computed(() => singles.value
    ? 'frozen players don\'t count'
    : 'frozen teams and teams sharing one of their players don\'t count');

const rules = computed(() => {
  const swap = isSwapLadder(props.ladder);
  let data = [
    {
      title: `When the lower ${unit.value} wins`,
      type: 'text',
      value: swap ? 'Swap places' : 'Winner goes above',
      hint: swap
          ? `The winner and loser trade places. Nobody else moves. If the higher ${unit.value} wins, nothing changes.`
          : `The winner takes the loser's place. The loser and every ${unit.value} between them move down one. If the higher ${unit.value} wins, nothing changes.`,
    },
    // Above and below are separate limits, each in its own direction; 0 is
    // "none that way", never "anyone" (audit A8).
    {
      title: 'Challenge range above',
      type: 'text',
      value: aboveLimitText(props.ladder.maxRankingDifference),
      hint: `The number of challengeable ${units.value} above a ${unit.value} that they can challenge (${skipRule.value})`,
    },
    {
      title: 'Challenge range below',
      type: 'text',
      value: belowLimitText(props.ladder.maxRankingDifferenceBelow),
      hint: `The number of challengeable ${units.value} below a ${unit.value} that they can challenge (${skipRule.value})`,
    },
    {
      title: 'Challenge expiry days',
      type: 'number',
      value: props.ladder.challengeExpiryDays,
      hint: 'The number of days players have to play a challenge before it is void',
    },
    ...(props.ladder.inactivityDemotionEnabled
        ? [{
          title: 'Play at least this often',
          type: 'text',
          value: `Every ${props.ladder.inactivityDemotionDays} days, or drop `
              + `${props.ladder.inactivityDemotionPlaces} place`
              + `${props.ladder.inactivityDemotionPlaces === 1 ? '' : 's'}`,
          hint: `Record a match at least every ${props.ladder.inactivityDemotionDays} days to keep your place. `
              + 'If you do not you move down, and you keep moving down every '
              + `${props.ladder.inactivityDemotionDays} days until you play. You are warned first.`,
        }]
        : []),
    {
      title: 'Penalty for ignoring or rejecting a challenge',
      type: 'text',
      value: challengeRejectionRuleToText(props.ladder.challengeRejectionRule, props.ladder.challengeRejectionMoveDownPlaces),
      hint: 'The penalty for ignoring or rejecting a challenge. Either no action, automatic loss, or movement down the ladder',
    },
    {
      title: 'Number of unrestricted challenges allowed',
      type: 'number',
      value: props.ladder.numberUnrestrictedChallenges,
      hint: 'The number of unrestricted challenges a player is allowed',
    },
  ];

  if (isAuthenticated.value && props.unrestrictedChallenges !== null) {
    data.push({
      title: 'Number of unrestricted challenges remaining for you',
      type: 'number',
      value: props.unrestrictedChallenges,
      hint: 'The number of unrestricted challenges you have remaining in this ladder',
    });
  }

  data.push(
      {
        title: 'Number of players classified as top players',
        type: 'number',
        value: props.ladder.topPlayersCount,
        hint: 'How many places at the top of the ladder use the top players\' challenge range',
      },
      {
        title: 'Challenge range above for top players',
        type: 'number',
        value: props.ladder.maxDifferenceTopPlayers,
        hint: `The number of challengeable ${units.value} above a 'top player' that they can challenge (${skipRule.value}). Challenges down the ladder follow the normal range below.`,
      },
      {
        title: 'Concurrent challenge mode',
        type: 'text',
        value: {0: 'One at a time', 1: 'One sent, unlimited received', 2: 'Unlimited'}[props.ladder.concurrentChallengeMode ?? 0] ?? 'One at a time',
        // Counted per TEAM. A member's second pairing carries its own cap, so
        // it is not held up while their first pairing plays its match.
        hint: singles.value
            ? 'Controls how many challenges a player can have at once'
            : 'Controls how many challenges a team can have at once. Each team a member plays in has its own',
      },
      ...(singles.value ? [] : [{
        title: 'Players in multiple teams',
        type: 'bool',
        value: props.ladder.allowMultipleTeamsPerPlayer === true,
        hint: props.ladder.allowMultipleTeamsPerPlayer === true
            ? 'A member can play in more than one team here, with a different line-up each time. Two teams sharing a member cannot challenge each other.'
            : 'Each member plays in one team in this ladder',
      }]),
      {
        title: 'Auto approve',
        type: 'bool',
        value: props.autoApprove,
        hint: props.autoApprove ? t('approve_fixture_auto_on_tip') : t('approve_fixture_auto_off_tip'),
      }
  );

  if (props.scoringSystemName) {
    data.push({
      title: 'Scoring system',
      type: 'text',
      value: props.scoringSystemName,
      hint: 'The scoring system used for this ladder',
    });
  }

  if(props.showSquashLevels) {
    data.push({
      title: 'Squash levels',
      type: 'bool',
      value: props.ladder.sendToLevels,
      hint: 'Are squash levels enabled for this ladder?',
    });
  }

  return data;
});
</script>

<template>
  <v-table class="rules-table">
    <tbody>
    <tr v-for="rule in rules" :key="rule.title">
      <td class="rule-title">{{ rule.title }}</td>
      <td class="text-right rule-value">
        <template v-if="rule.type === 'number' || rule.type === 'text'">
          {{ rule.value }}
        </template>
        <template v-else>
          <v-icon size="16" :color="rule.value ? 'success' : 'error'" :icon="rule.value ? 'mdi-check' : 'mdi-close'"></v-icon>
        </template>
      </td>
      <td>
        <hint-container :hint="rule.hint"></hint-container>
      </td>
    </tr>
    </tbody>
  </v-table>
</template>

<style scoped lang="scss">
td {
  padding: 10px 12px;
  border-bottom: solid 1px rgb(var(--v-theme-borderLight));
  font-size: 13px;
}

tr:last-child td {
  border-bottom: none;
}

.rule-title {
  font-weight: 500;
  color: rgb(var(--v-theme-charcoal));
}

.rule-value {
  font-weight: 700;
  color: rgb(var(--v-theme-text));
}
</style>
